import { SmartSendTokenInfo, SmartSendColumn } from '../../../Models/DataSources/SmartSend';
import {
    WaVariableMap, WaSaveMappingRequest, SmartSendMappingSuggestion,
} from '../../../Models/DataSources/SmartSendWhatsApp';
import { resolveColumnLabel } from '../columnLabel';

// SmartSend part B — the WhatsApp variable vocabulary. Pure module: no redux, no i18n, no React
// (same rule as `../suggestMapping`), so the self-test can exercise it directly.
//
// A WhatsApp template does not have ##tokens## — it has POSITIONAL placeholders `{{1}}..{{n}}`,
// and `PlaceholderCount` is the API's count of the DISTINCT indexes in the body (contract §5).
// `{{n}}` inside a URL button is neither counted nor mapped (v1.4 T-04) — the sender fills it
// per row through GetCallActionButtonClientIdVariable — which is why the count comes from the
// server and is never re-derived here from the body text.

export const STATIC_OPTION = '__static';

export interface WaVariable {
    index: number;          // 1..PlaceholderCount
    token: string;          // '{{1}}' — what the operator sees in the template
}

// 1..n, contiguous by construction. A template whose numbering is NOT contiguous is rejected in
// the picker (`IsContiguous=false` ⇒ `picker.card.notContiguous`), because step 4 of §3.4 only
// ever checks 1..PlaceholderCount and a gap would fail at send time instead of at pick time.
export const waVariables = (placeholderCount: number): WaVariable[] => {
    const n = Math.max(0, Math.floor(placeholderCount || 0));
    const out: WaVariable[] = [];
    for (let i = 1; i <= n; i += 1) out.push({ index: i, token: `{{${i}}}` });
    return out;
};

export const isBound = (m: WaVariableMap, index: number): boolean => {
    const b = m[index];
    return !!b && ((b.columnId != null && b.columnId > 0) || (b.staticValue != null && b.staticValue !== ''));
};

// The chips the mapping table offers as "כמו במייל". DELIBERATELY NOT a name-similarity match
// and DELIBERATELY NOT applied automatically — invariant 9 / 14-W3 §2: "אף עמודה אינה ממולאת
// אוטומטית — צ'יפים בלבד (K22)". The candidates are only columns the E-MAIL side already maps,
// capped at three, in the e-mail's own token order; a WhatsApp variable carries no name at all,
// so there is nothing to match on and nothing to score.
export const sameAsEmailCandidates = (
    tokens: SmartSendTokenInfo[],
    tokenMap: { [token: string]: number | null },
    columns: SmartSendColumn[],
    max = 3,
): { columnId: number; token: string }[] => {
    const live = new Set(columns.map((c) => c.ColumnID));
    const out: { columnId: number; token: string }[] = [];
    const seen = new Set<number>();
    tokens.forEach((tk) => {
        if (out.length >= max) return;
        const id = tokenMap[tk.Token];
        if (id == null || id <= 0 || !live.has(id) || seen.has(id)) return;
        seen.add(id);
        out.push({ columnId: id, token: tk.Token });
    });
    return out;
};

// The POST body of §5 `POST WhatsApp/Mapping/{id}`.
//
// 🔴 `MediaUrl` IS ALWAYS SENT when the template has one (v1.20 R17). `_SetMapping_Stage`
// documents `NULL` as "text template", NOT as "leave unchanged" — so a client that omits the
// field wipes the media on EVERY mapping save, step 7 then resolves NULL, and the campaign is
// CHARGED AND SENT for Meta to reject a media template with no media. The server also defaults
// a missing field to the template's own MediaUrl, but relying on that would leave the client
// unable to ever clear it when the operator switches to a text template — so it is explicit
// here, in both directions.
//
// Partial mappings are allowed by the SP; the SCREEN is what refuses to send one
// (`selectWaCanSend`). Sending only the bound rows keeps an untouched variable out of the UDTT
// rather than writing a row with two NULLs, which CK_..._OneSource would reject outright.
// 🔴 AND `SuppressEmail` IS THE EXACT OPPOSITE (v1.23), which is why it is a separate
// paragraph rather than another line in the one above. `_SetMapping_Stage` declares
// `@prm_SuppressEmail bit = NULL` with `ISNULL(@prm_SuppressEmail, SuppressEmail)` on UPDATE —
// so here NULL genuinely means "leave it alone", and the field is sent ONLY when the operator
// has moved the switch (`suppressEmailDirty`). §3.1 spells out what the other choice costs:
// "ברירת מחדל 0 שנכתבת ללא תנאי הייתה גורמת לכל שמירה שמשמיטה את הפרמטר לדרוס 1 ב-0 בשקט …
// והמייל יוצא בקמפיין שהמפעיל כיבה."
// ⚠️ Two fields, two NULL meanings, both correct. Do not "align" them.
export const buildWaSaveBody = (arg: {
    templateId: string;
    fromNumber: string | null;
    placeholderCount: number;
    mediaUrl: string | null;
    /** `null` ⇒ the operator has not touched the channel switch ⇒ the field is NOT sent. */
    suppressEmail?: boolean | null;
    map: WaVariableMap;
    /** 09-channel-units — the source on screen; sent only when known (> 0). */
    dataSourceId?: number | null;
    dataSourceVersionId?: number | null;
    /** 12-fix-wave 7.4(d) — the operator confirmed the source/version reset; sent only when true. */
    confirmReset?: boolean;
}): WaSaveMappingRequest => ({
    WhatsAppTemplateID: arg.templateId,
    FromNumber: arg.fromNumber ?? null,
    PlaceholderCount: arg.placeholderCount,
    MediaUrl: arg.mediaUrl ?? null,
    // 09-channel-units — the WhatsApp unit's own mapping row follows the source ON SCREEN (the
    // server re-binds it on channel 3). Spread, not `undefined`, for the reason SuppressEmail gives
    // below: absent is absent. The version travels only WITH its source — a version on its own
    // names nothing, and the server refuses it (400).
    ...(arg.dataSourceId != null && arg.dataSourceId > 0 ? { DataSourceID: arg.dataSourceId } : {}),
    ...(arg.dataSourceId != null && arg.dataSourceId > 0 && arg.dataSourceVersionId != null && arg.dataSourceVersionId > 0
        ? { DataSourceVersionID: arg.dataSourceVersionId } : {}),
    // Spread, not `SuppressEmail: undefined`: an explicit `undefined` key is dropped by
    // JSON.stringify today, but it also type-checks against `boolean | undefined` and would
    // survive any future switch to a serializer that emits `null` for it — which the server
    // would read as "not specified" only by luck. Absent is absent.
    ...(arg.suppressEmail == null ? {} : { SuppressEmail: !!arg.suppressEmail }),
    ...(arg.confirmReset ? { ConfirmReset: true } : {}),
    Map: waVariables(arg.placeholderCount)
        .filter((v) => isBound(arg.map, v.index))
        .map((v) => {
            const b = arg.map[v.index];
            return {
                VariableIndex: v.index,
                // Exactly one source per row — CK_CampaignWhatsAppVariableMap_OneSource. A
                // column wins when both are somehow present; `setWaVariable` already writes
                // them as a pair, so that case is defensive only.
                DataSourceColumnID: b.columnId != null && b.columnId > 0 ? b.columnId : null,
                StaticValue: b.columnId != null && b.columnId > 0 ? null : b.staticValue,
            };
        }),
});

// 🔴 WHICH CANDIDATE BELONGS TO WHICH VARIABLE — AND THE BUG THIS REPLACES IS WHY THE
// CUSTOMER MAPS EVERYTHING BY HAND.
//
// The call site read `candidates.find(c => !used.has(c.columnId))` and IGNORED THE VARIABLE
// ENTIRELY, so every unmapped row on the screen was offered THE SAME first free column. Three
// empty variables, three identical chips. That is not a suggestion per variable — it is one
// suggestion rendered N times, and an operator who accepts it once has to do the rest by hand
// anyway. The customer's complaint ("most of my time goes on mapping the same template again")
// is a direct consequence.
//
// The rule now: variable `{{k}}` is offered the k-th e-mail candidate, POSITIONALLY. The e-mail
// side's token order is the only ordering either side shares — a WhatsApp variable carries no
// name at all (`{{1}}`, not `##שם##`), so there is nothing to match on and nothing to score,
// which is exactly why v1.4 K22 forbade a similarity match. Position is the honest mapping, and
// it is STABLE: the same variable is offered the same column on every render.
//
// ⚠️ ALREADY-USED COLUMNS ARE NOT OFFERED, and the position is counted in the WHOLE candidate
// list, never in the list of free columns (QA BUG-11 / W-04): `{{k}}` is offered the k-th column
// the e-mail maps, and nothing when a variable already uses that column. The candidates are
// distinct, so accepting a positional chip takes only its own column and never moves what another
// row is offering. (Counting in the free list did: accepting `{{2}}` moved `{{3}}` and `{{4}}` one
// on.) A memory chip accepted on another row's candidate withdraws only that row's offer.
//
// ⚠️ AND THIS IS STILL A SUGGESTION. Invariant 9 / K22 stands: nothing is filled without an
// operator click. This function returns what to OFFER, never what to write.
export const suggestionForVariable = (
    variableIndex: number,
    candidates: { columnId: number; token: string }[],
    usedColumnIds: Set<number>,
): number | null => {
    if (!(variableIndex > 0)) return null;
    // 1-based variables, 0-based list. A variable past the end of the candidate list, or whose
    // column a variable already uses, gets nothing — an honest silence, not the next column.
    const c = candidates[variableIndex - 1];
    return c != null && !usedColumnIds.has(c.columnId) ? c.columnId : null;
};

// 🔴 THE DUPLICATE-HEADER RULE (contract §6: "בורר העמודות מציג chip «כותרת כפולה — נבחרה
// הראשונה» כשיש `SourceHeader` כפול"). It is a correctness rule, not a nicety, because the
// mapping does NOT travel as a ColumnID: `_SetMapping_Stage` stores the column's `SourceHeader`
// (it is the stability key across uploads, K22) and resolves it back on read as
// `MIN(ColumnID)` per `(DataSourceVersionID, SourceHeader)` — the same rule
// `DataSources_CompleteVersion:76-80` uses, because headers are not unique within a version.
//
// So an operator who picks the SECOND column carrying a header gets the FIRST one's values at
// send time, with nothing on screen to say so. That is a wrong-value-silently hazard on a
// per-message-charged send, and the chip is the only thing that surfaces it.
//
// Returns the ids of every column whose header is shared, so the caller can mark the row
// whichever of them was chosen — the sentence "the first column was selected" is true in both
// cases, and it is the case where it is NOT the first that it has to be shown.
export const duplicateHeaderColumnIds = (columns: SmartSendColumn[]): Set<number> => {
    const byHeader = new Map<string, number[]>();
    (columns ?? []).forEach((c) => {
        // Trim, because two uploads of the same file can differ by trailing whitespace in a
        // header and the SP's equality would still fold them together.
        const key = (c.SourceHeader ?? '').trim();
        if (key === '') return;      // a blank header cannot be resolved by header at all
        const list = byHeader.get(key);
        if (list) list.push(c.ColumnID); else byHeader.set(key, [c.ColumnID]);
    });
    const out = new Set<number>();
    byHeader.forEach((ids) => { if (ids.length > 1) ids.forEach((id) => out.add(id)); });
    return out;
};

// ══ 08-mapping-memory — "USED LAST TIME" ═══════════════════════════════════════════════════════
//
// The memory is a PREVIOUS EXPLICIT CHOICE by an operator of this sub-account for this exact
// (template, variable) — not a guess scored from a name, which is the whole reason it may go FIRST.
// It is still only an OFFER: the chip writes nothing until the operator clicks it (invariant 9 /
// K22), one named row at a time, exactly like the positional offer below it. Nothing here returns
// a list of things to apply, and nothing may ever be built on top of it that does
// (`suggestMapping.ts:16-44` — the bulk path is gone and stays gone).
//
// Both screens resolve memory the SAME way — by header, server-side — so the rules that decide
// whether a row can be offered live here once, and the e-mail screen imports them.

// A row is offered only when the remembered header is IN the version being mapped: the server
// resolved it there (`ColumnInVersion`, a positive `DataSourceColumnID` — the LOWEST ColumnID when
// the header is duplicated) AND that id is one of the columns on screen. `=== true`, not truthy:
// this is a convenience, so anything that is not plainly a yes is a no.
// Anything else is "no memory for this variable" and the caller moves on down its chain — a header
// that has left the source must not SILENCE the offer the operator had before this feature existed.
export const usableMemoryColumnId = (
    row: SmartSendMappingSuggestion | null | undefined,
    liveColumnIds: Set<number>,
): number | null => {
    if (!row || row.ColumnInVersion !== true) return null;
    const id = row.DataSourceColumnID;
    return id != null && id > 0 && liveColumnIds.has(id) ? id : null;
};

// Plan v2 §4.1 (VariableKey: WhatsApp '1','2' · e-mail the token) — the two matching rules, and
// they are deliberately different.
// WhatsApp: `VariableKey` is the VariableIndex as a decimal string, and the table carries the index
// as `tok.Token` = String(index) — so the two are compared as NUMBERS.
export const memoryRowForVariable = (
    variableIndex: number,
    rows: SmartSendMappingSuggestion[] | null | undefined,
): SmartSendMappingSuggestion | null => {
    if (!(variableIndex > 0)) return null;
    return (rows ?? []).find((r) => !!r && Number(r.VariableKey) === variableIndex) ?? null;
};

// E-mail: `VariableKey` is the token exactly as it travels in SmartSendTokenMapRow.Token (the server
// writes it as-is) — so it is compared AS-IS, the same exact, case-sensitive string the token map
// itself is keyed on.
export const memoryRowForToken = (
    token: string,
    rows: SmartSendMappingSuggestion[] | null | undefined,
): SmartSendMappingSuggestion | null =>
    (rows ?? []).find((r) => !!r && r.VariableKey === token) ?? null;

// The chip's NAME — DisplayName, falling back to SourceHeader — through `resolveColumnLabel`, the one
// resolution every surface on this screen names a column with, applied to the LIVE column the row
// resolved to (the very DataSourceColumns row the server read that DisplayName from). So the chip
// and the dropdown can never name one column two ways, and the name is never blank. The row's own
// fields are only a fallback for a column that is not on screen, which `usableMemoryColumnId`
// already refuses to offer.
export const memoryColumnName = (
    row: SmartSendMappingSuggestion,
    columns: SmartSendColumn[] | null | undefined,
): string => {
    const col = (columns ?? []).find((c) => c.ColumnID === row.DataSourceColumnID);
    if (col) return resolveColumnLabel(col);
    return (row.DisplayName && row.DisplayName.trim())
        || (row.SourceHeader && row.SourceHeader.trim())
        || String(row.DataSourceColumnID ?? '');
};

export type WaSuggestionPick =
    | { source: 'memory'; columnId: number; row: SmartSendMappingSuggestion }
    | { source: 'sameAsEmail'; columnId: number };

// 🔴 THE WHATSAPP CHAIN: memory → the positional "כמו במייל" offer → nothing. MEMORY WINS.
// A `{{k}}` has no name, so NAME SIMILARITY IS NEVER A LINK HERE — on '1', '2' it is the module
// that produced 1.0000-confidence wrong columns, and the tab therefore never sets the table's
// `fallbackToSimilarity`. The positional link is `suggestionForVariable` (counted in the whole
// candidate list since QA BUG-11), so a variable with no usable memory sees exactly that.
// ⚠️ Memory ignores `usedColumnIds`, and that is not an oversight: skipping used columns is how the
// positional offer shares ONE candidate pool across rows, while a memory row belongs to this
// variable alone — two variables remembered on one column (a name printed twice) is a real
// template, and hiding the second offer would be the memory contradicting itself.
export const composeWaSuggestion = (
    variableIndex: number,
    memoryRows: SmartSendMappingSuggestion[] | null | undefined,
    liveColumnIds: Set<number>,
    candidates: { columnId: number; token: string }[],
    usedColumnIds: Set<number>,
): WaSuggestionPick | null => {
    const row = memoryRowForVariable(variableIndex, memoryRows);
    const memoryColumnId = usableMemoryColumnId(row, liveColumnIds);
    if (row && memoryColumnId != null) return { source: 'memory', columnId: memoryColumnId, row };
    const positional = suggestionForVariable(variableIndex, candidates, usedColumnIds);
    return positional == null ? null : { source: 'sameAsEmail', columnId: positional };
};

// Static values are capped at 1000 chars server-side (400 DATA_INCORRECT above it) and the
// sender's own `fn_DataSources_WhatsApp_Param` strips control characters 0-31. Trimming to the
// same limit here means the operator sees the cut while typing instead of losing a save.
export const STATIC_VALUE_MAX = 1000;
export const clampStaticValue = (v: string): string => (v ?? '').slice(0, STATIC_VALUE_MAX);
